import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Truck, ExternalLink } from "lucide-react";
import { fetchOrders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { testIds } from "@/constants/testIds/b2b";
import SortSelect, { applySort, cmpDate, cmpNum } from "@/components/SortSelect";
import { fmtCAD } from "@/lib/currency";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  invoiced: "bg-sky-50 text-sky-700 border-sky-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const MY_ORDERS_SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "total_desc", label: "Total high → low" },
  { value: "total_asc", label: "Total low → high" },
];

const MY_ORDERS_COMPARATORS = {
  newest: cmpDate((o) => o.created_at, "desc"),
  oldest: cmpDate((o) => o.created_at, "asc"),
  total_desc: cmpNum((o) => o.subtotal, "desc"),
  total_asc: cmpNum((o) => o.subtotal, "asc"),
};

function relativeTimeFromIso(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const diffSec = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function MyOrders() {
  const { user, loading: authLoading } = useAuth();
  const loc = useLocation();
  const newOrderId = loc.state?.newOrderId;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => (newOrderId ? { [newOrderId]: true } : {}));
  const [sort, setSort] = useState("newest");

  const sortedOrders = useMemo(
    () => applySort(orders, sort, MY_ORDERS_COMPARATORS),
    [orders, sort],
  );

  useEffect(() => {
    (async () => {
      try { setOrders(await fetchOrders()); } catch { /* not logged in */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (authLoading || loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-foreground/70" data-testid="orders-loading">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="text-xl font-semibold mb-2">Sign in to view your orders</h1>
        <Link to="/login" className="text-primary underline text-sm">Sign in</Link>
      </div>
    );
  }

  const toggle = (id) => setExpanded({ ...expanded, [id]: !expanded[id] });

  return (
    <div className="page-card" data-testid={testIds.myOrdersPage}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Wholesale account</p>
            <h1 className="text-2xl md:text-3xl font-semibold mt-0.5">Orders</h1>
          </div>
          {orders.length > 0 && (
            <SortSelect
              value={sort}
              onChange={setSort}
              options={MY_ORDERS_SORT_OPTIONS}
              testid="my-orders-sort"
            />
          )}
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1,2,3].map((i) => <div key={i} className="h-14 rounded bg-secondary animate-pulse" />)}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-md" data-testid={testIds.myOrdersEmpty}>
            <p className="font-medium mb-1">No orders yet</p>
            <p className="text-sm text-muted-foreground">When you submit a wholesale order, it&apos;ll show up here.</p>
            <Link to="/shop" className="inline-flex mt-4 px-4 py-2 rounded bg-primary text-primary-foreground text-sm">
              Browse products
            </Link>
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 bg-secondary border-b border-border text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              <div className="col-span-3">Order</div>
              <div className="col-span-2">Placed</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Units</div>
              <div className="col-span-3 text-right">Total</div>
            </div>
            {sortedOrders.map((o, idx) => {
              const isNew = newOrderId === o.id;
              const open = !!expanded[o.id];
              const styleCls = STATUS_STYLE[o.status] || "bg-secondary text-foreground border-border";
              const units = o.items.reduce((s, it) => s + it.quantity, 0);
              return (
                <div key={o.id} data-testid={testIds.orderCard} className={`${idx > 0 ? "border-t border-border" : ""} ${isNew ? "bg-primary/[0.03]" : ""}`}>
                  <button
                    onClick={() => toggle(o.id)}
                    className="w-full grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm text-left hover:bg-secondary/40"
                  >
                    <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                      {open ? <ChevronDown size={14} strokeWidth={1.5} /> : <ChevronRight size={14} strokeWidth={1.5} />}
                      <span className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</span>
                      {isNew && <span className="text-[10px] uppercase tracking-[0.12em] text-primary">new</span>}
                    </div>
                    <div className="col-span-6 md:col-span-2 text-muted-foreground">
                      {new Date(o.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] border rounded ${styleCls}`}>
                        {o.status} · {o.terms}
                      </span>
                      {o.last_reminder_sent_at && (o.status === "pending" || o.status === "invoiced") && (
                        <div
                          data-testid={`my-order-reminder-${o.id}`}
                          title={new Date(o.last_reminder_sent_at).toLocaleString()}
                          className="mt-1 text-[10px] text-amber-700"
                        >
                          Reminder sent {relativeTimeFromIso(o.last_reminder_sent_at)}
                        </div>
                      )}
                    </div>
                    <div className="col-span-6 md:col-span-2 tabular-nums text-muted-foreground">{units}</div>
                    <div className="col-span-6 md:col-span-3 text-right tabular-nums font-medium">{fmtCAD(o.subtotal)}</div>
                  </button>

                  {open && (
                    <div className="px-4 pb-4 pt-1 border-t border-border bg-secondary/30">
                      <ul className="text-sm">
                        {o.items.map((it, i) => (
                          <li key={i} className="py-1.5 flex justify-between gap-3">
                            <span className="min-w-0 truncate">
                              {it.name}{it.size ? ` · ${it.size}` : ""} <span className="text-muted-foreground">× {it.quantity}</span>
                            </span>
                            <span className="tabular-nums shrink-0">{fmtCAD(it.line_total)}</span>
                          </li>
                        ))}
                      </ul>
                      {o.shipping_address && <p className="mt-3 text-xs text-muted-foreground">Ship to: {o.shipping_address}</p>}
                      {o.notes && <p className="text-xs text-muted-foreground">Notes: {o.notes}</p>}
                      {(o.tracking_number || o.tracking_url) && (
                        <div
                          data-testid={`my-order-tracking-${o.id}`}
                          className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800"
                        >
                          <Truck size={13} strokeWidth={1.6} className="shrink-0" />
                          <span className="font-medium">Shipped</span>
                          {o.tracking_carrier && <span>· {o.tracking_carrier}</span>}
                          {o.tracking_number && <span className="font-mono">· {o.tracking_number}</span>}
                          <a
                            href={o.tracking_url || `https://www.google.com/search?q=${encodeURIComponent(o.tracking_number)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 underline ml-1"
                          >
                            Track <ExternalLink size={11} strokeWidth={1.6} />
                          </a>
                        </div>
                      )}
                      {o.last_reminder_sent_at && (
                        <p
                          data-testid={`my-order-reminder-detail-${o.id}`}
                          className="mt-2 text-xs text-amber-700"
                        >
                          Payment reminder last sent {relativeTimeFromIso(o.last_reminder_sent_at)}
                          {` · ${new Date(o.last_reminder_sent_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`}
                          {o.reminder_count > 1 ? ` · ${o.reminder_count} total` : ""}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
